import { useCallback, useMemo } from "react";
import {
  useIntegrationSyncMap,
  useOrgOnboardingStatus,
  useOrgPlan,
  type ModelName,
  type OrgPlanName,
  type SyncAction,
} from "@tellescope/tanstack-query";
import { resolveOrgFeatures } from "./resolve-org-features";
import { ACTION_SYNC_GATED_PLANS, MODEL_FEATURE_MAP } from "./plan-features";
import type { FeatureAvailability, FeatureName } from "./types";

export interface UseOrgFeaturesResult {
  plan: OrgPlanName | undefined;
  isLoading: boolean;
  features: Record<FeatureName, FeatureAvailability>;
  /** Whether a feature is part of the plan (unknown plan ⇒ everything enabled). */
  isFeatureEnabled: (name: FeatureName) => boolean;
  /**
   * Whether an integration would auto-sync this model action — `true` unless an
   * integration that touches the model has that action's sync turned off. Models
   * no integration touches fall back to `true`. Only enforced for plans in
   * {@link ACTION_SYNC_GATED_PLANS}; every other (or unknown) plan returns
   * `true`. Pure integration logic, with no feature gating folded in
   * (see {@link isFeatureActionEnabled}).
   */
  isActionSyncEnabled: (modelName: ModelName, action: SyncAction) => boolean;
  /**
   * Whether a model action is enabled: the model's feature must be enabled, and
   * (when an integration auto-syncs the model) that integration's sync behavior
   * for the action must fire. Models no integration touches fall back to enabled.
   */
  isFeatureActionEnabled: (modelName: ModelName, action: SyncAction) => boolean;
  /** The full availability (enabled + setup status + powering integration) for a feature. */
  getFeatureAvailability: (name: FeatureName) => FeatureAvailability;
}

/**
 * Composes {@link useOrgPlan} + {@link useOrgOnboardingStatus} with
 * {@link resolveOrgFeatures} into the app's feature-gating surface. Memoized.
 *
 * Exported for feature-only consumers (routing / nav) that have no model or
 * record context; record-permission call sites should use
 * {@link useAppPermissionQuery}, which folds this in.
 */
export const useOrgFeatures = (): UseOrgFeaturesResult => {
  const { plan, isLoading: planLoading } = useOrgPlan();
  const { status, isLoading: statusLoading } = useOrgOnboardingStatus();
  const { syncMap, isLoading: syncMapLoading } = useIntegrationSyncMap();

  const features = useMemo(() => resolveOrgFeatures({ plan, status }), [plan, status]);

  const isFeatureEnabled = useCallback((name: FeatureName) => features[name].enabled, [features]);
  const isActionSyncEnabled = useCallback(
    (modelName: ModelName, action: SyncAction) => {
      if (!plan || !ACTION_SYNC_GATED_PLANS.includes(plan)) return true;

      const behavior = syncMap[modelName];
      return behavior ? behavior[action] : true;
    },
    [plan, syncMap]
  );
  const isFeatureActionEnabled = useCallback(
    (modelName: ModelName, action: SyncAction) => {
      const feature = MODEL_FEATURE_MAP[modelName];
      if (feature && !features[feature].enabled) return false;

      return isActionSyncEnabled(modelName, action);
    },
    [features, isActionSyncEnabled]
  );
  const getFeatureAvailability = useCallback((name: FeatureName) => features[name], [features]);

  return useMemo(
    () => ({
      plan,
      isLoading: planLoading || statusLoading || syncMapLoading,
      features,
      isFeatureEnabled,
      isActionSyncEnabled,
      isFeatureActionEnabled,
      getFeatureAvailability,
    }),
    [
      features,
      getFeatureAvailability,
      isActionSyncEnabled,
      isFeatureActionEnabled,
      isFeatureEnabled,
      plan,
      planLoading,
      statusLoading,
      syncMapLoading,
    ]
  );
};
